// Pure mood data: target vectors, transition matrix, dwell ranges, articulation.

import type { AxisVector, Mood } from '@shared/parameterState';
import { AXES } from '@shared/parameterState';

export const MOOD_TARGETS: Record<Mood, AxisVector> = {
  abyssal: { density: 0.12, register: 0.15, brightness: 0.2, harmonicity: 0.45, space: 0.9 },
  tidepool: { density: 0.45, register: 0.6, brightness: 0.7, harmonicity: 0.3, space: 0.4 },
  storm: { density: 0.85, register: 0.55, brightness: 0.6, harmonicity: 0.8, space: 0.3 },
};

/** Off-diagonal transition weights (never self-transition). */
export const TRANSITION_MATRIX: Record<Mood, Partial<Record<Mood, number>>> = {
  abyssal: { tidepool: 0.75, storm: 0.25 },
  tidepool: { abyssal: 0.45, storm: 0.55 },
  storm: { tidepool: 0.7, abyssal: 0.3 },
};

/** Dwell range in SECONDS per mood (minutes * 60). */
export const DWELL_RANGE_SEC: Record<Mood, [number, number]> = {
  abyssal: [4 * 60, 7 * 60],
  tidepool: [3 * 60, 5 * 60],
  storm: [3 * 60, 5 * 60],
};

export interface MoodArticulation {
  wildProb: number;
  /** weights for ['strike','grain']. */
  excitation: { strike: number; grain: number };
  decayScale: number;
  silenceProb: number;
  silenceLenSec: [number, number];
  gestureAnchorProb: number;
}

export const MOOD_ARTICULATION: Record<Mood, MoodArticulation> = {
  abyssal: {
    wildProb: 0.02,
    excitation: { strike: 0.9, grain: 0.1 },
    decayScale: 2.5,
    silenceProb: 0.3,
    silenceLenSec: [10, 30],
    gestureAnchorProb: 0.08,
  },
  tidepool: {
    wildProb: 0.1,
    excitation: { strike: 0.4, grain: 0.6 },
    decayScale: 0.8,
    silenceProb: 0,
    silenceLenSec: [10, 30],
    gestureAnchorProb: 0,
  },
  storm: {
    wildProb: 0.35,
    excitation: { strike: 0.2, grain: 0.8 },
    decayScale: 0.4,
    silenceProb: 0,
    silenceLenSec: [10, 30],
    gestureAnchorProb: 0,
  },
};

/** 2D mood-pad landmark coordinates in [0,1]^2. */
export const PAD_LANDMARKS: Record<Mood, { x: number; y: number }> = {
  abyssal: { x: 0.15, y: 0.85 },
  tidepool: { x: 0.5, y: 0.25 },
  storm: { x: 0.85, y: 0.75 },
};

export function lerpVector(a: AxisVector, b: AxisVector, t: number): AxisVector {
  const out = {} as AxisVector;
  for (const axis of AXES) out[axis] = a[axis] + (b[axis] - a[axis]) * t;
  return out;
}

export function cloneVector(v: AxisVector): AxisVector {
  return { ...v };
}
